import "@/styles/globals.css";
import { CookieConsent } from "@/parts/cookieConsent";
import { useConsent } from "@/utils/consent";
import { VisualEditing } from "@sanity/visual-editing/next-pages-router";
import { Analytics } from "@vercel/analytics/react";
import { AnimatePresence, motion } from "framer-motion";
import type { AppProps } from "next/app";
import { Spectral } from "next/font/google";
import localFont from "next/font/local";
import { useRouter } from "next/router";
import Script from "next/script";
import { useEffect } from "react";

const META_PIXEL_ID = "1347835520127578";
const GA_MEASUREMENT_ID = "G-KLD974D2D1";
const GOOGLE_ADS_ID = "AW-18342306774";

const spectral = Spectral({
  weight: "500",
  subsets: ["latin"],
  variable: "--font-spectral",
});

const detai = localFont({
  src: [
    {
      path: "./detai.otf",
      weight: "900",
      style: "normal",
    },
  ],
  variable: "--font-detai",
});

export default function App({
  Component,
  pageProps,
}: AppProps<{ draftMode?: boolean }>) {
  const router = useRouter();
  const consent = useConsent();

  // The pixel only fires PageView on the initial load; client-side
  // navigations have to report themselves.
  useEffect(() => {
    const handleRouteChange = () => {
      (window as any).fbq?.("track", "PageView");
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);

  return (
    <main className={`${spectral.variable} ${detai.variable}`}>
      {/* Tracking only loads after an explicit "Accepteren" in the cookie
          banner; withdrawing consent stops it from loading on the next
          page load. */}
      {consent === "granted" && (
        <>
          <Script id="meta-pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${META_PIXEL_ID}');
              fbq('track', 'PageView');
            `}
          </Script>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          {/* One gtag.js loader serves both products; GA4 and Google Ads are
              registered via their own config calls. GA4's enhanced measurement
              handles client-side navigations, so no routeChange handler here. */}
          <Script id="google-gtag" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}');
              gtag('config', '${GOOGLE_ADS_ID}');
            `}
          </Script>
        </>
      )}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={router.asPath}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{
            opacity: { duration: 0.12, ease: "linear" },
            y: {
              type: "spring",
              stiffness: 260,
              damping: 30,
              mass: 0.45,
            },
          }}
        >
          <Component {...pageProps} />
        </motion.div>
      </AnimatePresence>
      {pageProps.draftMode && (
        <>
          <VisualEditing zIndex={9999} />
          <a
            href={`/api/disable-draft?pathname=${encodeURIComponent(
              router.asPath,
            )}`}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] bg-black text-white font-display text-sm px-4 py-2 shadow-lg hover:opacity-80"
          >
            Preview: concepten zichtbaar — klik om te sluiten
          </a>
        </>
      )}
      <CookieConsent />
      <Analytics />
    </main>
  );
}
